import { FC, useEffect, useState, useCallback } from 'react';
import { Table, Button, message, Space, Modal, Tag } from 'antd';
import { fetchActiveOffsets, fetchAllOffsets, deleteOffset, deactivateOffset, clearOffsetPoints, PriceOffset, PriceOffsetWithPointCount } from '../../api/priceOffset';
import TimeDisplay from '../TimeDisplay';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface OffsetListProps {
  offsets?: PriceOffset[];
  offsetsWithCount?: PriceOffsetWithPointCount[];
  onRefreshNeeded?: () => void;
  onView?: (offset: PriceOffset) => void;
  showHistoryMode?: boolean;
}

const OffsetList: FC<OffsetListProps> = ({ 
  offsets: propOffsets, 
  offsetsWithCount: propOffsetsWithCount, 
  onRefreshNeeded, 
  onView, 
  showHistoryMode = false 
}) => {
  const [offsets, setOffsets] = useState<PriceOffset[]>([]);
  const [offsetsWithCount, setOffsetsWithCount] = useState<PriceOffsetWithPointCount[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOffsets = useCallback(async () => {
    if (propOffsets) {
      setOffsets(propOffsets);
      return;
    }
    if (propOffsetsWithCount) {
      setOffsetsWithCount(propOffsetsWithCount);
      return;
    }
    
    setLoading(true);
    try {
      if (showHistoryMode) {
        const data = await fetchAllOffsets();
        setOffsetsWithCount(data);
      } else {
        const data = await fetchActiveOffsets();
        setOffsets(data);
      }
    } catch (error) {
      console.error('获取价格偏移设置失败', error);
    } finally {
      setLoading(false);
    }
  }, [propOffsets, propOffsetsWithCount, showHistoryMode]);

  useEffect(() => {
    if (propOffsets) {
      setOffsets(propOffsets);
    } else if (propOffsetsWithCount) {
      setOffsetsWithCount(propOffsetsWithCount);
    } else {
      fetchOffsets();
    }
  }, [propOffsets, propOffsetsWithCount, fetchOffsets]);


  const handleDeactivate = async (productCode: string) => {
    try {
      await deactivateOffset(productCode);
      message.success('取消激活成功');
      refreshData();
    } catch (error) {
      console.error('取消激活失败', error);
      message.error('取消激活失败');
    }
  };

  const handleClearPoints = async (offsetId: number, productCode: string) => {
    Modal.confirm({
      title: '确认清空点数据',
      icon: <ExclamationCircleOutlined />,
      content: `确定要清空产品 ${productCode} 的所有关联点数据吗？此操作不可恢复！`,
      onOk: async () => {
        try {
          await clearOffsetPoints(offsetId);
          message.success('清空点数据成功');
          refreshData();
        } catch (error) {
          console.error('清空点数据失败', error);
          message.error('清空点数据失败');
        }
      }
    });
  };

  const refreshData = () => {
    if (propOffsets || propOffsetsWithCount) {
      onRefreshNeeded?.();
    } else {
      fetchOffsets();
    }
  };

  const getColumns = () => {
    const baseColumns = [
      { 
        title: '产品代码', 
        dataIndex: showHistoryMode ? ['priceOffset', 'productCode'] : 'productCode', 
        key: 'productCode' 
      },
      { 
        title: '偏移量', 
        dataIndex: showHistoryMode ? ['priceOffset', 'offsetValue'] : 'offsetValue', 
        key: 'offsetValue' 
      },
    ];

    if (showHistoryMode) {
      baseColumns.push(
        {
          title: '状态',
          dataIndex: ['priceOffset', 'isActive'],
          key: 'isActive',
          render: (isActive: boolean) => (
            <Tag color={isActive ? 'green' : 'default'}>
              {isActive ? '激活' : '未激活'}
            </Tag>
          )
        },
        {
          title: '关联点数据',
          dataIndex: 'pointCount',
          key: 'pointCount',
          render: (count: number) => `${count} 条`
        }
      );
    }

    baseColumns.push(
      { 
        title: '设置时间', 
        dataIndex: showHistoryMode ? ['priceOffset', 'updatedAt'] : 'updatedAt', 
        key: 'updatedAt',
        render: (updatedAt: string) => (
          <TimeDisplay value={updatedAt} />
        )
      },
      {
        title: '操作',
        key: 'action',
        render: (_: any, record: any) => {
          const offsetData = showHistoryMode ? record.priceOffset : record;
          const pointCount = showHistoryMode ? record.pointCount : 0;
          
          return (
            <Space>
              {onView && (
                <Button 
                  type="primary" 
                  size="small" 
                  onClick={() => onView(offsetData)}
                >
                  查看
                </Button>
              )}
              {offsetData.isActive && (
                <Button 
                  size="small" 
                  onClick={() => handleDeactivate(offsetData.productCode)}
                >
                  取消激活
                </Button>
              )}
              {showHistoryMode && pointCount > 0 && (
                <Button 
                  size="small" 
                  danger
                  onClick={() => handleClearPoints(offsetData.id, offsetData.productCode)}
                >
                  清空点数据
                </Button>
              )}
            </Space>
          );
        },
      }
    );

    return baseColumns;
  };

  const dataSource = showHistoryMode ? offsetsWithCount : offsets;
  const title = showHistoryMode ? "价格偏移历史记录" : "当前激活的偏移设置";

  return (
    <Table
      title={() => title}
      dataSource={dataSource}
      columns={getColumns()}
      rowKey={showHistoryMode ? (record: any) => record.priceOffset.id : "id"}
      loading={loading && !propOffsets && !propOffsetsWithCount}
    />
  );
};

export default OffsetList; 